// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { useTranslator } from '@gandi/translate';
import { Button } from '@gandi/ui';

import {
  UnavailableRowContainer,
  UnavailableErrorContainer,
} from 'src/Domain/UnavailableRowContainer';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectWhoisUrl } from 'src/modules/config';

import { fetchFallback } from '../../../modules/actions/fallbacks';
import type { AvailabilityEventData, Fallback } from '../../../types';

type Props = {
  availability?: AvailabilityEventData['availability'],
  error?: boolean,
  fqdn: string,
};

function Action({ fallback, fqdn }: { fallback: Fallback, fqdn: string }) {
  const { __ } = useTranslator();

  switch (true) {
    case fallback.transferable:
      return (
        <>
          <span>{__('Is it yours? Transfer it')}</span>
          <Button to={`/domain/transfer?search=${fqdn}`}>
            {__('Transfer')}
          </Button>
        </>
      );
    case fallback.renewable:
      return (
        <span>
          {__('Is it yours?')}{' '}
          <Link to={`/domain/renew?search=${fqdn}`}>{__('Renew it')}</Link>
        </span>
      );
    case fallback.restorable:
      return (
        <>
          <span>{__('Is it yours? Restore it')}</span>
          <Button to={`/domain/renew?search=${fqdn}`}>{__('Restore')}</Button>
        </>
      );

    default:
      return null;
  }
}

export default function Unavailable({
  availability,
  error,
  fqdn,
}: Props): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const whoisURL = useAppSelector(selectWhoisUrl);

  const [fallback, setFallback] = React.useState();

  const isErrorRef = React.useRef(
    (error && availability !== 'unavailable') || availability === 'error',
  );

  React.useEffect(() => {
    const checkForFallbacks: () => Promise<any> = async () => {
      return dispatch(fetchFallback(fqdn));
    };

    checkForFallbacks()
      .then(response => {
        setFallback(response);
      })
      .catch(() => {
        isErrorRef.current = true;
      });
  }, [dispatch, fqdn]);

  if (isErrorRef.current) {
    return <Unavailable.Error fqdn={fqdn} />;
  }

  return (
    <UnavailableRowContainer
      availabilityIconName={availability}
      availabilityText={
        <>
          {__('No! This domain is not available!')}{' '}
          <a target="_blank" rel="noopener" href={`${whoisURL}?search=${fqdn}`}>
            {__('See WHOIS')}
          </a>
        </>
      }
      fqdn={fqdn}
    >
      {fallback ? <Action fallback={fallback} fqdn={fqdn} /> : null}
    </UnavailableRowContainer>
  );
}

Unavailable.Error = function UnavailableError({
  fqdn,
}: {
  fqdn: string,
}): React.MixedElement {
  const { __ } = useTranslator();
  return (
    <UnavailableErrorContainer
      fqdn={fqdn}
      info={__('Sorry, something unexpected happened. Retry later.')}
    />
  );
};
